import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface CommonFabricDataResult<T> {
  value: T | undefined;
  isMixed: boolean;
  setValue: (next: T | undefined) => void;
}

interface Aggregate<T> {
  value: T | undefined;
  isMixed: boolean;
}

function aggregate<T>(objects: fabric.Object[], key: string): Aggregate<T> {
  if (objects.length === 0) return { value: undefined, isMixed: false };
  const first = (objects[0].data as Record<string, T> | undefined)?.[key];
  for (let i = 1; i < objects.length; i++) {
    const v = (objects[i].data as Record<string, T> | undefined)?.[key];
    if (v !== first) return { value: undefined, isMixed: true };
  }
  return { value: first, isMixed: false };
}

export function useCommonFabricData<T>(
  objects: fabric.Object[],
  key: string,
  editor: FabricEditor,
): CommonFabricDataResult<T> {
  const objectSet = useMemo(() => new Set(objects), [objects]);
  const read = useCallback(() => aggregate<T>(objects, key), [objects, key]);
  const [state, setState] = useState<Aggregate<T>>(read);

  useEffect(() => {
    setState(read());
    const off = editor.on('object:modified', (e) => {
      if (objectSet.has(e.target as fabric.Object)) setState(read());
    });
    return () => off();
  }, [editor, objectSet, read]);

  const setValue = useCallback(
    (next: T | undefined) => {
      for (const obj of objects) {
        editor.objects.setData(obj, key, next);
      }
      setState({ value: next, isMixed: false });
    },
    [editor, objects, key],
  );

  return { value: state.value, isMixed: state.isMixed, setValue };
}
